import { useEffect, useRef, useState, forwardRef } from 'react';
import { Image, Layer, Stage } from 'react-konva';

import { useImageEditorStore } from './ImageEditorStoreProvider';
import type { AllStoreType } from './store/imageEditor.type';
import { editorConfig } from './utils/editorConfig';
import MaskLayer from './components/MaskLayer';
import type { MaskLayerHandle } from './components/MaskLayer';

const KonvaImage = ({
  imageUrl,
  isMobile,
}: {
  imageUrl: File | string;
  isMobile: boolean;
}) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new window.Image();
    img.src =
      typeof imageUrl === 'string' ? imageUrl : URL.createObjectURL(imageUrl);
    img.onload = () => {
      setImage(img);
    };
    return () => {
      URL.revokeObjectURL(img.src);
    };
  }, [imageUrl]);

  if (!image) {
    return null;
  }

  return (
    <Image
      image={image}
      x={0}
      y={0}
      width={isMobile ? 300 : editorConfig.width}
      height={isMobile ? 300 : editorConfig.height}
    />
  );
};

interface ImageEditorProps {
  onExportMask?: (maskDataUrl: string) => void;
  disabled?: boolean;
}

const ImageEditor = forwardRef<{ exportMask: () => void }, ImageEditorProps>(
  ({ disabled }) => {
    const { currentLine, addPoint, addLine, removeCurrentLine, imageUrl } =
      useImageEditorStore<AllStoreType>((state) => state);
    const stageRef = useRef<any>(null);
    const maskLayerRef = useRef<MaskLayerHandle>(null);

    const [isMobile, setIsMobile] = useState(false); // to check for mobile

    useEffect(() => {
      if (window.innerWidth < 640) {
        setIsMobile(true);
      }
    }, [window.innerWidth]);

    const handleStageMouseDown = (e: any) => {
      if (disabled) return;

      const stage = stageRef?.current;
      const pos = stage?.getPointerPosition();
      const x = pos?.x || 0;
      const y = pos?.y || 0;

      addLine([x, y]);
    };

    const handleStageMouseMove = (e: any) => {
      if (disabled) return;

      if (!currentLine) {
        return;
      }

      const stage = stageRef?.current;
      const pos = stage?.getPointerPosition();
      const x = pos?.x || 0;
      const y = pos?.y || 0;

      addPoint([x, y]);
    };

    const handleUp = () => {
      if (disabled) return;

      removeCurrentLine();
    };

    return (
      <div className="w-fit">
        <div className="rounded-lg bg-accent p-4">
          <Stage
            className="bg-accent"
            width={isMobile ? 300 : editorConfig.width}
            height={isMobile ? 300 : editorConfig.height}
            ref={stageRef}
            onMouseDown={handleStageMouseDown}
            onMouseMove={handleStageMouseMove}
            onMouseUp={handleUp}
            onTouchStart={handleStageMouseDown}
            onTouchMove={handleStageMouseMove}
            onTouchEnd={handleUp}
          >
            {imageUrl && (
              <Layer>
                <KonvaImage imageUrl={imageUrl} isMobile={isMobile} />
              </Layer>
            )}
            <MaskLayer ref={maskLayerRef} />
          </Stage>
        </div>
      </div>
    );
  }
);

ImageEditor.displayName = 'ImageEditor';

export default ImageEditor;
